import React, { useCallback, useEffect, useState } from "react";
import { useAuthContext } from "../context/AuthContext";
import { useAlert } from "../components/AlertModal";
import {
  fetchShiftLocations,
  createShiftLocation,
  updateShiftLocation,
  deleteShiftLocation,
} from "../api/shiftApi";
import "../styles/ShiftMasterPage.css";

function ShiftLocationMasterPage() {
  const { user: userInfo, loading } = useAuthContext();
  const { showAlert } = useAlert();

  const [items, setItems] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newName, setNewName] = useState("");
  const [newShortName, setNewShortName] = useState("");
  const [newDisplayOrder, setNewDisplayOrder] = useState(0);
  const [newIsActive, setNewIsActive] = useState(true);
  const [newColor, setNewColor] = useState("");
  const [newNote, setNewNote] = useState("");

  const loadData = useCallback(async () => {
    if (!userInfo?.store_id) return;

    setLoadingPage(true);
    try {
      const data = await fetchShiftLocations({
        storeId: userInfo.store_id,
        isActive: true,
      });
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error(error);
      showAlert("勤務場所一覧の取得に失敗しました。");
    } finally {
      setLoadingPage(false);
    }
  }, [userInfo?.store_id, showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      showAlert("場所名を入力してください。");
      return;
    }

    setSaving(true);
    try {
      await createShiftLocation({
        store: userInfo.store_id,
        name: newName.trim(),
        short_name: newShortName.trim(),
        display_order: Number(newDisplayOrder) || 0,
        is_active: newIsActive,
        color: newColor,
        note: newNote,
      });

      setNewName("");
      setNewShortName("");
      setNewDisplayOrder(0);
      setNewIsActive(true);
      setNewColor("");
      setNewNote("");
      await loadData();
      showAlert("勤務場所を追加しました。");
    } catch (error) {
      console.error(error);
      showAlert(error?.details?.detail || "勤務場所追加に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (id, key, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const handleUpdate = async (item) => {
    try {
      await updateShiftLocation(item.id, {
        name: item.name,
        short_name: item.short_name || "",
        display_order: Number(item.display_order) || 0,
        is_active: !!item.is_active,
        color: item.color || "",
        note: item.note || "",
      });
      showAlert("更新しました。");
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert(error?.details?.detail || "更新に失敗しました。");
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("削除しますか？");
    if (!ok) return;

    try {
      await deleteShiftLocation(id);
      showAlert("削除しました。");
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert(error?.details?.detail || "削除に失敗しました。");
    }
  };

  if (loading || !userInfo) return <div>読み込み中...</div>;

  return (
    <div className="shift-master-page">
      <h2 className="shift-master-title">勤務場所マスター</h2>

      <div className="shift-master-card">
        <h3 className="shift-master-subtitle">新規追加</h3>
        <div className="shift-master-form-row">
          <input
            type="text"
            className="shift-master-input"
            placeholder="場所名"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            type="text"
            className="shift-master-input small"
            placeholder="略称"
            value={newShortName}
            onChange={(e) => setNewShortName(e.target.value)}
          />
          <input
            type="number"
            className="shift-master-input small"
            placeholder="表示順"
            value={newDisplayOrder}
            onChange={(e) => setNewDisplayOrder(e.target.value)}
          />
          <input
            type="text"
            className="shift-master-input small"
            placeholder="#ffaaaa"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
          />
          <label className="shift-master-checkbox">
            <input
              type="checkbox"
              checked={newIsActive}
              onChange={(e) => setNewIsActive(e.target.checked)}
            />
            有効
          </label>
          <input
            type="text"
            className="shift-master-input"
            placeholder="メモ"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <button
            type="button"
            className="shift-master-btn"
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "追加中..." : "追加"}
          </button>
        </div>
      </div>

      <div className="shift-master-card">
        <h3 className="shift-master-subtitle">一覧</h3>

        {loadingPage ? (
          <div>読み込み中...</div>
        ) : (
          <div className="shift-master-table-wrap">
            <table className="shift-master-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>場所名</th>
                  <th>略称</th>
                  <th>表示順</th>
                  <th>色</th>
                  <th>有効</th>
                  <th>メモ</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>
                      <input
                        type="text"
                        className="shift-master-input"
                        value={item.name || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "name", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="shift-master-input small"
                        value={item.short_name || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "short_name", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="shift-master-input small"
                        value={item.display_order ?? 0}
                        onChange={(e) =>
                          handleFieldChange(item.id, "display_order", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="shift-master-input small"
                        value={item.color || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "color", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <label className="shift-master-checkbox">
                        <input
                          type="checkbox"
                          checked={!!item.is_active}
                          onChange={(e) =>
                            handleFieldChange(item.id, "is_active", e.target.checked)
                          }
                        />
                        有効
                      </label>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="shift-master-input"
                        value={item.note || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "note", e.target.value)
                        }
                      />
                    </td>
                    <td className="shift-master-actions">
                      <button
                        type="button"
                        className="shift-master-btn"
                        onClick={() => handleUpdate(item)}
                      >
                        更新
                      </button>
                      <button
                        type="button"
                        className="shift-master-btn danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan="8">データがありません。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShiftLocationMasterPage;